import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SwitchToPreviewButton from "./SwitchToPreviewButton";
import ProjectName from "./ProjectName";
import SharePreviewButton from "./SharePreviewButton";
import MenuButton from "./MenuButton";
import StagedAIModel from "./StagedAIModel";
import { IconShare, IconHelpCircle, IconMoon, IconSun } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import RedoButton from "./RedoButton";
import { MiroShareButton, GoogleShareButton } from "./ShareModal";
import UndoButton from "./UndoButton";
import AddModelButton from "./AddModelButton";
import { ImageButton } from "./FloorColourButton";
import StoryboardLinkButton from "./StoryboardLinkButton";
import ExportProjectButton from "./ExportProjectButton";
import MCPConnectButton from "./MCPConnectButton";
import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import { useAccountStore } from "../zustand/account";
import ImageModal from "./ImageModal";
import EditorSceneNavigator from "./EditorSceneNavigator";
import EditorCameraPresetToolbar from "./EditorCameraPresetToolbar";
import StoryboardToolbarIcon from "./StoryboardToolbarIcon";

import { getMiroBoard, sendStoryboardToMiro, getUserMiroFrames, getUserGoogleSlidesThumbAndLength, exportToGSlides, saveUserScreenshotsAPI, getProjectGuestsAPI, deleteUserMiroEntry, deleteUserGoogleEntry, getGoogleSyncEntry, getMiroSyncEntry } from "../api";

import { useForm } from '@mantine/form';
import { nanoid } from 'nanoid'

import { Tooltip, TextInput, Spoiler, Select, Card, HoverCard, Button, Tabs, Text, Group, Loader, Modal, ActionIcon, LoadingOverlay, Alert, Box, Anchor } from '@mantine/core';

import { toolbar, modal } from "../styleFile.js"
import { getEditorChromeTheme, chromeToolbarGroupStyle, CHROME_TOOLBAR_GROUPS_GAP, CHROME_TOOLBAR_ICON_GLYPH } from "../utils/editorChromeTheme";
import { CHROME_ICON_HIT, CONTROL_RADIUS } from "../utils/uiChromeTokens";
import {
  isAiTextTo3dEnabled,
  AI_TEXT_TO_3D_UNAVAILABLE_TITLE,
  AI_TEXT_TO_3D_UNAVAILABLE_BODY,
  AI_TEXT_TO_3D_WATERMARK,
} from "../config/aiTextTo3d";


export default function TopProjectAppBar({ mode, projectKey }) {
  const { undoActions, redoActions } = useObjectStore(
    useShallow((s) => ({ undoActions: s.undoActions, redoActions: s.redoActions }))
  );
  const { viewportThemeOverride, toggleViewportThemeOverride } = useObjectStore(
    useShallow((s) => ({
      viewportThemeOverride: s.viewportThemeOverride,
      toggleViewportThemeOverride: s.toggleViewportThemeOverride,
    }))
  );
  const isDarkTopBar = viewportThemeOverride === "dark";
  const topBarTheme = getEditorChromeTheme(isDarkTopBar);

  const { helpOpen, setHelpOpen } = useAccountStore();

  const [helpHover, setHelpHover] = useState(false)
  const [viewportThemeHover, setViewportThemeHover] = useState(false)

  const iconColour = {
    normal: topBarTheme.icon,
    hover: topBarTheme.iconHover,
    border: topBarTheme.controlBorder,
    hoverBg: topBarTheme.hoverBg,
    disabledBg: topBarTheme.disabledBg,
    disabledIcon: topBarTheme.disabledIcon,
  }

  const toolbarActions = (
    <Group
      gap={0}
      wrap="nowrap"
      justify="flex-end"
      style={chromeToolbarGroupStyle(iconColour)}
    >
      <ExportProjectButton projectKey={projectKey} iconColour={iconColour} inGroup />
      <MCPConnectButton projectKey={projectKey} iconColour={iconColour} inGroup />
      <SharePreviewButton projectKey={projectKey} iconColour={iconColour} inGroup />
      <StoryboardLinkButton projectKey={projectKey} iconColour={iconColour} inGroup />
      <SwitchToPreviewButton mode={mode} projectKey={projectKey} iconColour={iconColour} inGroup />
    </Group>
  );

  return (
    <>
      <div
        style={{
          ...toolbar.toolbarContainer,
          backgroundColor: topBarTheme.bg,
          borderBottom: `1px solid ${topBarTheme.border}`,
        }}
      >
        {mode === "editor" ? (
          <>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Group gap={CHROME_TOOLBAR_GROUPS_GAP} wrap="nowrap">
                <MenuButton iconColour={iconColour} />
                <ProjectName textColor={topBarTheme.title} />
                <Tooltip label="Basic instructions">
                  <ActionIcon
                    size={CHROME_ICON_HIT}
                    radius={CONTROL_RADIUS}
                    variant="transparent"
                    color={!helpHover ? iconColour.normal : iconColour.hover}
                    onPointerOver={() => setHelpHover(true)}
                    onPointerLeave={() => setHelpHover(false)}
                    onClick={() => setHelpOpen(!helpOpen)}
                    aria-label="Basic instructions"
                    style={{
                      backgroundColor: helpHover ? topBarTheme.hoverBg : "transparent",
                      border: helpHover ? `1px solid ${topBarTheme.controlBorder}` : "1px solid transparent",
                    }}
                  >
                    <IconHelpCircle size={CHROME_TOOLBAR_ICON_GLYPH} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label={
                    viewportThemeOverride === "dark"
                      ? "Switch to light mode"
                      : "Switch to dark mode"
                  }
                >
                  <ActionIcon
                    size={CHROME_ICON_HIT}
                    radius={CONTROL_RADIUS}
                    variant="transparent"
                    color={!viewportThemeHover ? iconColour.normal : iconColour.hover}
                    onPointerOver={() => setViewportThemeHover(true)}
                    onPointerLeave={() => setViewportThemeHover(false)}
                    onClick={toggleViewportThemeOverride}
                    style={{
                      backgroundColor: viewportThemeHover ? topBarTheme.hoverBg : "transparent",
                      border: viewportThemeHover ? `1px solid ${topBarTheme.controlBorder}` : "1px solid transparent",
                      verticalAlign: "middle",
                    }}
                  >
                    {viewportThemeOverride === "dark" ? <IconMoon size={CHROME_TOOLBAR_ICON_GLYPH} /> : <IconSun size={CHROME_TOOLBAR_ICON_GLYPH} />}
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Box>
            <Box style={{ flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Group gap={CHROME_TOOLBAR_GROUPS_GAP} wrap="nowrap" style={{ ...toolbar.toolbarCentralGroup }}>
                <Group gap={0} wrap="nowrap" style={chromeToolbarGroupStyle(iconColour)}>
                  <UndoButton undoActions={undoActions} redoActions={redoActions} iconColour={iconColour} inGroup />
                  <RedoButton iconColour={iconColour} inGroup />
                </Group>
                <EditorSceneNavigator iconColour={iconColour} />
                <EditorCameraPresetToolbar iconColour={iconColour} />
              </Group>
            </Box>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              {toolbarActions}
            </Box>
          </>
        ) : mode === "storyboard" ? (
          <>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <Group gap={16} wrap="nowrap">
                <MenuButton iconColour={iconColour} framed />
                <ProjectName textColor={topBarTheme.title} />
                <Tooltip label="Basic instructions">
                  <ActionIcon
                    size={CHROME_ICON_HIT}
                    radius={CONTROL_RADIUS}
                    variant="transparent"
                    color={!helpHover ? iconColour.normal : iconColour.hover}
                    onPointerOver={() => setHelpHover(true)}
                    onPointerLeave={() => setHelpHover(false)}
                    onClick={() => setHelpOpen(!helpOpen)}
                    aria-label="Basic instructions"
                    style={{
                      backgroundColor: helpHover ? topBarTheme.hoverBg : "transparent",
                      border: helpHover ? `1px solid ${topBarTheme.controlBorder}` : "1px solid transparent",
                    }}
                  >
                    <IconHelpCircle size={CHROME_TOOLBAR_ICON_GLYPH} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label={
                    viewportThemeOverride === "dark"
                      ? "Switch to light mode"
                      : "Switch to dark mode"
                  }
                >
                  <ActionIcon
                    size={CHROME_ICON_HIT}
                    radius={CONTROL_RADIUS}
                    variant="transparent"
                    color={!viewportThemeHover ? iconColour.normal : iconColour.hover}
                    onPointerOver={() => setViewportThemeHover(true)}
                    onPointerLeave={() => setViewportThemeHover(false)}
                    onClick={toggleViewportThemeOverride}
                    style={{
                      backgroundColor: viewportThemeHover ? topBarTheme.hoverBg : "transparent",
                      border: viewportThemeHover ? `1px solid ${topBarTheme.controlBorder}` : "1px solid transparent",
                    }}
                  >
                    {viewportThemeOverride === "dark" ? <IconMoon size={CHROME_TOOLBAR_ICON_GLYPH} /> : <IconSun size={CHROME_TOOLBAR_ICON_GLYPH} />}
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Box>
            <Box style={{ flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Anchor
                component={Link}
                to={`/editor/${projectKey}`}
                size="sm"
                fw={500}
                c={topBarTheme.link}
                underline="never"
                style={{ fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}
                styles={{ root: { "&:hover": { color: topBarTheme.linkHover } } }}
              >
                ← Back to Editor
              </Anchor>
            </Box>
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 0,
              }}
            />
          </>
        ) : (
          <Box style={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
            {toolbarActions}
          </Box>
        )}
      </div>
      <UsersModal projectKey={projectKey} />
      <AddAIModal projectKey={projectKey} />
      <ImageModal />
    </>
  );
}

const UsersModal = ({ projectKey }) => {
  const { userAccessModalOpen } = useObjectStore(
    useShallow((s) => ({ userAccessModalOpen: s.userAccessModalOpen }))
  );

  const [guests, setGuests] = useState([])

  const getGuests = useCallback(() => {
    getProjectGuestsAPI(projectKey).then(res =>
      setGuests(res.projectGuests)
    )

  }, [projectKey])

  useEffect(() => {
    getGuests()
  }, [getGuests, userAccessModalOpen])




  return (
    <Modal
      opened={userAccessModalOpen}
      // onClose={onUserModalCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          backgroundColor: 'white',
          border: '2px solid #000',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div>
            <Text fw={700} size="xl">
              Set user access
            </Text>
          </div>
          <div>
            <form style={{ marginTop: 0 }}>
              <input type="checkbox" />
              <label style={{ marginLeft: 10 }}>
                Logged-out preview access?
              </label>
            </form>
          </div>
          <div />
          <div>
            <Text fw={600} size="md">
              Project guests
            </Text>
            <form style={{ marginTop: 5 }}>
              <input placeholder="Enter user email address" type="text" />
              <button>Submit</button>
            </form>
          </div>
          <div>
            {userAccessModalOpen && guests.length > 0 ?
              guests.map((mapped, i) =>
                <Text key={i} style={{ marginTop: 5 }} size="sm" fw={500}>
                  {mapped.email} – {mapped.access} access
                </Text>)
              :
              <Text style={{ marginTop: 5 }} size="sm" fw={500}>
                No guest editors.
              </Text>}
          </div>
        </div>
      </div>
    </Modal>
  );
}



const AddAIModal = ({ projectKey }) => {

  const {
    addAIModalOpen,
    getAIModels,
    addObject,
    sendAIPrompt,
    currentObjectArtboard,
    aiModels,
    removeAIModel,
    arrangeUndoRedo
  } = useObjectStore();

  const [sending, setSending] = useState(false)
  const aiEnabled = isAiTextTo3dEnabled()

  const handleClick = (e) => {
    e.preventDefault()
    if (!aiEnabled) return
    setSending(true)
    sendAIPrompt(e.target[0].value, projectKey)
      .then(() => {
        setSending(false)
        getAIModels()
      })
      .catch(() => setSending(false))
  }

  const handleSelect = (e) => {
    const newID = nanoid()
    // send update to zustand first (so we have state to grab for undo array)
    addObject({
      currentObjectArtboard,
      category: 'aiModel',
      object: e.prompt,
      undo: false,
      id: newID,
      position: null,
      scale: null,
      rotation: null,
      matrix: null,
      pose: null,
      aiKey: e.id,
      imageTexture: null,
      image: null,
      curved: false,
      curveAmount: 3,
      bGColour: null
    })
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "objectAdd",
      artboard: currentObjectArtboard,
      objectID: newID,
      undo: false
    })

  }


  const handleDeleteClick = (e) => {
    removeAIModel(e)
  }


  return (
    <Modal
      opened={addAIModalOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div
        style={{
          overflow: 'auto',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          height: 400,
          backgroundColor: 'white',
          border: '2px solid #000',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div>
            <Text fw={700} size="lg">
              Generate a 3D object using AI (beta)
            </Text>
          </div>

          {!aiEnabled && (
            <Alert variant="light" color="gray" title={AI_TEXT_TO_3D_UNAVAILABLE_TITLE} mt="sm" mb="sm" styles={{ title: { fontWeight: 700 } }}>
              {AI_TEXT_TO_3D_UNAVAILABLE_BODY}
            </Alert>
          )}

          <div />
          <div>
            <Text size="sm">
              {aiEnabled
                ? "Enter a prompt to generate a 3D object for your project."
                : "AI generation will be available on paid plans."}
            </Text>
            <form style={{ marginTop: 5, position: "relative" }} onSubmit={(e) => handleClick(e)}>
              {!aiEnabled && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255, 255, 255, 0.88)",
                    pointerEvents: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#374151",
                  }}
                >
                  {AI_TEXT_TO_3D_WATERMARK}
                </div>
              )}
              <input
                placeholder={aiEnabled ? "Enter prompt for AI" : "Coming soon on paid plans"}
                type="text"
                disabled={!aiEnabled || sending}
              />
              <button disabled={!aiEnabled || sending} type="submit">{sending ? "sending" : "Generate"}</button>
            </form>
          </div>
          <div
            style={{
              position: "relative",
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 8,
              marginTop: 8,
              minHeight: 120,
            }}
          >
            {!aiEnabled && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255, 255, 255, 0.75)",
                  pointerEvents: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  color: "#374151",
                }}
              >
                {AI_TEXT_TO_3D_WATERMARK}
              </div>
            )}
            {aiModels &&
              aiModels.map((mapped, i) => (
                <div key={i}>
                  <div style={{ height: 6, width: 'auto' }}>
                    <StagedAIModel prompt={mapped.prompt} />
                  </div>
              <button onClick={() => handleSelect(mapped)}
              >Add {mapped.prompt}</button>
              <button onClick={() => handleDeleteClick(mapped.id)}
              >Delete</button>
                </div>
              ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export const ExportToAPIButton = ({ projectKey, storyboardToolbar = false }) => {

  const [hover, setHover] = useState(false)
  const [opened, { open, close }] = useDisclosure(false);

  const setTabValue = useObjectStore((s) => s.setTabValue);



  const handleClick = () => {
    setTabValue("scenes")
  }

  const shareIcon = storyboardToolbar ? (
    <StoryboardToolbarIcon
      label="Share / export storyboard"
      onClick={() => {
        handleClick();
        open();
      }}
    >
      <IconShare size={20} />
    </StoryboardToolbarIcon>
  ) : (
    <Tooltip label="Export storyboard">
      <ActionIcon
        style={toolbar.toolbarActionIcon}
        size="sm"
        radius={12}
        variant="transparent"
        color={!hover ? "#424242" : "#868e96"}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={() => {
          handleClick();
          open();
        }}
      >
        <IconShare size={18} />
      </ActionIcon>
    </Tooltip>
  );

  return (<>
    {storyboardToolbar ? (
      shareIcon
    ) : (
    <div style={toolbar.toolbarActionIconContainer}>
      {shareIcon}
    </div>
    )}

    {opened && <ExportModalContent closeFunction={close} opened={opened} projectKey={projectKey} />}

  </>
  )
}










export const ExportModalContent = ({ projectKey, closeFunction, opened }) => {


  const { screenshots, setScreenshotStatus } = useObjectStore(
    useShallow((s) => ({
      screenshots: s.screenshots,
      setScreenshotStatus: s.setScreenshotStatus,
    }))
  );



  // console.log(opened)

  // const screenshotArray = "blablabla"
  const screenshotArray = Object.values(screenshots)

  async function saveScreenshots() {
    // console.log(screenshotArray)
    saveUserScreenshotsAPI({ projectKey: projectKey, screenshots: [...screenshotArray] })
  }

  // const [openPicker, authResponse] = useDrivePicker();

  const [haveEntry, setHaveEntry] = useState(false)
  const [haveGoogleEntry, setHaveGoogleEntry] = useState(false)
  const [sending, setSending] = useState(false)
  const [fileSelected, setFileSelected] = useState(false)
  const [miroFileSelected, setMiroFileSelected] = useState(false)
  const [selectedFile, setSelectedFile] = useState({ name: '', thumbnail: '', type: '', id: '', url: '' })
  const [selectedMiroFile, setSelectedMiroFile] = useState({ name: '', thumbnail: '', id: '', url: '', frames: [] })
  const [selectedSlide, setSelectedSlide] = useState(0)
  const [selectedFrame, setSelectedFrame] = useState('None')
  const [miroError, setMiroError] = useState(false)
  const [miroErrorMessage, setMiroErrorMessage] = useState('No error')
  const [loadingAfterGoogleSubmit, setLoadingAfterGoogleSubmit] = useState(false)
  const [loadingAfterMiroSubmit, setLoadingAfterMiroSubmit] = useState(false)

  useEffect(() => {
    setFileSelected(false)
    getMiroSyncEntry().then((res) => { res === 200 && setHaveEntry(true) })
    getGoogleSyncEntry().then((res) => { res === 200 && setHaveGoogleEntry(true) })
  }, [])

  const deleteEntry = () => {
    deleteUserMiroEntry()
    setHaveEntry(false)
  }
  const deleteGoogleSlidesEntry = () => {
    deleteUserGoogleEntry()
    setHaveGoogleEntry(false)
  }

  const handleClose = () => {
    closeFunction()
    setScreenshotStatus(false)
    setFileSelected(false)
  }


  const handleExportClick = (id, slidesLength) => {
    setSending(true)
    saveUserScreenshotsAPI({ projectKey: projectKey, screenshots: [...screenshotArray] })
      .then(() => exportToGSlides({ presentationID: id, index: slidesLength, projectKey: projectKey }))
      .then(() => setScreenshotStatus(false))
      .then(() => setSending(false))

  }

  const handlePickerSelect = (type, id, url) => {
    getUserGoogleSlidesThumbAndLength(id)
      .then((res) => {
        console.log(res)
        setSelectedFile({
          name: res.title,
          type: type,
          id: id,
          url: url,
          thumbnail: res.imageURL,
          slides: [...Array(res.slidesLength).keys()].map(i => i + 1)
        })
        setFileSelected(true)
        setLoadingAfterGoogleSubmit(false)
      }
      )
  }


  const formMiro = useForm({
    initialValues: {
      url: ''
    },

    validate: {
      url: (value) => (/^https:\/\/miro\.com\/app\/board\/([^/]+)\/?/.test(value) ? null : 'Invalid URL'),
      // url: (value) => (/^https:\/\/miro\.com\/app\/board\/([^/]+)\//.test(value) ? null : 'Invalid URL'),
    },
  });

  const handleMiroSelect = (boardId, url, name, picture) => {

    getUserMiroFrames(boardId)
      .then((res) => {
        setSelectedMiroFile({
          name: name,
          id: boardId,
          url: url,
          thumbnail: picture,
          frameData: [{ id: "None", name: "None" }, ...res]
        })
        setMiroFileSelected(true)
        setLoadingAfterMiroSubmit(false)
      })

  }

  function extractMiroId(url) {
    // Regular expression to match the desired part of the URL
    // const regex = /\/board\/([^/]+)\/?/;
    const regex = /\/board\/([^/]+)(=\/)?/;


    // Match the URL against the regex
    const match = url.match(regex);

    // If a match is found, return the extracted ID
    if (match) {
      return (match[1]);
    } else {
      // Return an error message if no match is found
      return ('Invalid URL.');
    }
  }





  const generateError = () => {
    setMiroErrorMessage("Incomplete export. Was your frame too small for the storyboards?")
    setMiroError(true)
  }

  const handleMiroExportClick = (boardId, frameId) => {
    setSending(true)
    saveUserScreenshotsAPI({ projectKey: projectKey, screenshots: [...screenshotArray] })
      .then(() => sendStoryboardToMiro({ boardId: boardId, projectKey: projectKey, frameId: frameId }))
      .then((res) => (res === 201) ? setScreenshotStatus(false) : generateError())
      .then(() => setScreenshotStatus(false))
      .then(() => setSending(false))
  }

  const handleReset = () => {
    setMiroError(false)
    setMiroErrorMessage('No error')
    setMiroFileSelected(false)
  }

  const form = useForm({
    initialValues: {
      url: ''
    },

    validate: {
      // url: (value) => (/^https:\/\/docs\.google\.com\/presentation\/d\/([^/]+)\/edit(?:#[^/]+)?$/.test(value) ? null : 'Invalid URL'),
      url: (value) => (
        /^https:\/\/docs\.google\.com\/presentation\/d\/([^/]+)\/?(?:\/[^/]+)?(?:#.*)?$/.test(value)
          ? null
          : 'Invalid URL'
      ),
    },
  });


  const selectFunction = (url) => {
    setLoadingAfterMiroSubmit(true)
    const id = extractMiroId(url)
    saveScreenshots().then(() => setScreenshotStatus(false))
    getMiroBoard(id).then(res => handleMiroSelect(id, url, res.name, res.picture))
    formMiro.reset();

  }


  const handleSubmitGoogleURL = (url) => {
    setLoadingAfterGoogleSubmit(true)
    const id = extractId(url)
    saveScreenshots().then(() => setScreenshotStatus(false))
    handlePickerSelect('pres', id, url)
    form.reset();
  }

  function extractId(url) {
    // Regular expression to match the desired part of the URL
    const regex = /\/d\/([^/]+)\/edit/;

    // Match the URL against the regex
    const match = url.match(regex);

    // If a match is found, return the extracted ID
    if (match) {
      return (match[1]);
    } else {
      // Return an error message if no match is found
      return ('Invalid URL.');
    }
  }



  const clearMiroFileAndURL = () => {
    setMiroFileSelected(false)
  }
  const clearFileAndURL = () => {
    setFileSelected(false)
  }


  return (
    <Modal
      zIndex={1000}
      yOffset={48}
      radius={4}
      styles={{
        content: { maxHeight: "calc(100vh - 96px)", overflowY: "auto" },
      }}
      withCloseButton={false}
      size={560}
      opened={opened}
      onClose={() => handleClose()}
    >
      <LoadingOverlay loaderProps={{ color: 'black' }} visible={sending} zIndex={1000} overlayProps={{ radius: 4, blur: 2 }} />
      <LoadingOverlay loaderProps={{ color: 'black' }} visible={loadingAfterGoogleSubmit} zIndex={1000} overlayProps={{ radius: 4, blur: 2 }} />
      <LoadingOverlay loaderProps={{ color: 'black' }} visible={loadingAfterMiroSubmit} zIndex={1000} overlayProps={{ radius: 4, blur: 2 }} />


      <Group>
        <Text size="xl" fw={700}>Export your storyboards</Text>
        <Group justify="center">
          <HoverCard width={280} shadow="md" zIndex={1001}>
            <HoverCard.Target>
              <IconHelpCircle size={20} />
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size="sm" >
                Storyboard images incomplete? Try closing and reopening this modal and exporting again.
              </Text>
            </HoverCard.Dropdown>
          </HoverCard>
        </Group></Group>
      <Tabs defaultValue="miro">
        <Tabs.List>
          <Tabs.Tab value="miro">
            Miro
          </Tabs.Tab>
          <Tabs.Tab value="google-slides" >
            Google Slides
          </Tabs.Tab>
        </Tabs.List>

        {/* MIRO */}
        <Tabs.Panel value="miro">
          <div style={modal.modalTabContents}>
            {haveEntry ? <><Button onClick={() => deleteEntry()}
              style={{
                width: 130, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
              }}>Unlink Miro</Button>
              {!miroFileSelected && <Spoiler maxHeight={20} showLabel="Show more" hideLabel="Hide" mb={40}>
                <Text style={{ backgroundColor: "transparent" }} size='sm' >
                  This is a 3D ideas tool. It is not designed to secure sensitive content.
                  Avoid creating, uploading, or sharing sensitive content. <a href="https://www.matchboxxr.com/policies" target="_blank">Read more</a></Text>
              </Spoiler>}
              {miroFileSelected && <Button
                onClick={() => clearMiroFileAndURL()}
                style={{
                  width: 160, marginTop: 20, marginBottom: 20, paddingBottom: 1, marginLeft: 5, inline: true, backgroundColor: 'black', borderRadius: 4
                }}>Enter new URL</Button>}


              {
                miroError ?
                  <Card key={selectedMiroFile.id} withBorder radius={4} p={10} style={{ marginBottom: 20, borderRadius: 4 }}
                    shadow="0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>{miroErrorMessage}<Button
                      onClick={() => handleReset()}
                      style={{
                        width: 100, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
                      }}>Reset</Button></div></Card>

                  : miroFileSelected ?
                    <Card key={selectedMiroFile.id} withBorder radius={4} p={0} style={{ marginBottom: 20, borderRadius: 4 }}
                      shadow="0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)">
                      <Suspense fallback={<Loader color="black" />}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {/* THUMBNAIL */}
                          <a href={selectedMiroFile.url} target="_blank" style={{ cursor: 'pointer', textDecoration: 'none', backgroundColor: 'none', border: 'none' }}>
                            {selectedMiroFile.thumbnail ? <img referrerPolicy="no-referrer" src={selectedMiroFile.thumbnail} style={{ width: "100%", maxWidth: 520, height: "auto", aspectRatio: "16 / 9", objectFit: "cover", position: "relative", display: "block" }} /> :
                              <div style={{ width: "100%", maxWidth: 520, aspectRatio: "16 / 9", backgroundColor: '#F4B400' }} />}</a>
                          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 16, paddingRight: 16, paddingTop: 8 }}>
                            {/* TITLE */}
                            <Text size="xl" fw={700} mt={8}><span style={{ display: "block", whiteSpace: "nowrap", maxWidth: "100%", textOverflow: "ellipsis", overflow: "hidden", paddingLeft: 4, paddingRight: 4, backgroundColor: "#fac710" }}>
                              {selectedMiroFile.name}</span></Text>

                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 16 }}>
                              {/* BOARD SELECTION DROPDOWN */}

                              <div style={{ width: 175 }} >
                                <Select
                                  comboboxProps={{ zIndex: 1000 }}
                                  label="Frame selected"
                                  placeholder="None"
                                  // data={['None',...selectedMiroFile.frames]}

                                  data={selectedMiroFile.frameData.map((obj, i) => ({ value: obj.id, label: obj.name ? i + ": " + obj.name : i + ": (no title)" }))}
                                  value={selectedFrame}
                                  onChange={(val) => setSelectedFrame(val)}
                                /></div>

                              {/* SHARE BUTTON */}
                              <Button
                                onClick={() => handleMiroExportClick(selectedMiroFile.id, selectedFrame)}
                                style={{
                                  width: 100, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
                                }}>Export</Button>
                            </div>
                          </div>
                        </div>
                      </Suspense> </Card> :
                    <form onSubmit={formMiro.onSubmit((values) => selectFunction(values.url))}>
                      <TextInput
                        withAsterisk
                        label="Miro URL"
                        placeholder="Enter your board URL"
                        {...formMiro.getInputProps('url')}
                      />
                      <Button style={{
                        width: 100, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
                      }} type="submit">Submit</Button>
                    </form>

              }
            </>

              : <MiroShareButton projectKey={projectKey} />
            }
          </div>
        </Tabs.Panel>

        {/* GOOGLE */}
        <Tabs.Panel value="google-slides">
          <div style={modal.modalTabContents}>
            {haveGoogleEntry ? <><Button
              onClick={() => deleteGoogleSlidesEntry()}
              style={{
                width: 150, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
              }}>Unlink Google</Button>
              {fileSelected && <Button
                onClick={() => clearFileAndURL()}
                style={{
                  width: 160, marginTop: 20, marginBottom: 20, paddingBottom: 1, marginLeft: 5, inline: true, backgroundColor: 'black', borderRadius: 4
                }}>Enter new URL</Button>}

              {!fileSelected && <Spoiler maxHeight={20} showLabel="Show more" hideLabel="Hide" mb={40}>
                <Text style={{ backgroundColor: "transparent" }} size='sm' >
                  This is a 3D ideas tool. It is not designed to secure sensitive content.
                  Avoid creating, uploading, or sharing sensitive content. <a href="https://www.matchboxxr.com/policies" target="_blank">Read more</a></Text>
              </Spoiler>}
              {/* SELECTED GOOGLE SLIDES DOC */}
              {
                fileSelected ? (selectedFile.type === 'pres') ?
                  <Card key={selectedFile.id} withBorder radius={4} p={0} style={{ marginBottom: 20, borderRadius: 4 }}
                    shadow="0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)">
                    <Suspense fallback={<Loader color="black" />}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* THUMBNAIL */}
                        <a href={selectedFile.url} target="_blank" style={{ cursor: 'pointer', textDecoration: 'none', backgroundColor: 'none', border: 'none' }}>
                          {selectedFile.thumbnail ? <img referrerPolicy="no-referrer" src={selectedFile.thumbnail} style={{ width: "100%", maxWidth: 520, height: "auto", position: "relative", display: "block" }} /> :
                            <div style={{ width: "100%", maxWidth: 520, aspectRatio: "16 / 9", backgroundColor: '#F4B400' }} />}</a>
                        <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 16, paddingRight: 16, paddingTop: 8 }}>
                          {/* TITLE */}
                          <Text size="xl" fw={700} mt={8}><span style={{ display: "block", whiteSpace: "nowrap", maxWidth: "100%", textOverflow: "ellipsis", overflow: "hidden", paddingLeft: 4, paddingRight: 4, backgroundColor: "#fac710" }}>{selectedFile.name}</span></Text>

                          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 16 }}>
                            {/* SLIDE SELECTION DROPDOWN */}

                            <div style={{ width: 145 }} >
                              <Select
                                comboboxProps={{ zIndex: 1000 }}
                                label="Slide selected"
                                placeholder="0"
                                data={[
                                  "0",
                                  ...selectedFile.slides.map((slide) => slide.toString())
                                ]}
                                // data={[0,...selectedFile.slides]}
                                value={selectedSlide}
                                onChange={(val) => setSelectedSlide(val)}
                              /></div>

                            {/* SHARE BUTTON */}
                            <Button
                              onClick={() => handleExportClick(selectedFile.id, selectedSlide)}
                              style={{
                                width: 100, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
                              }}>Export</Button>
                          </div>
                        </div>
                      </div>
                    </Suspense> </Card> :
                  <Suspense fallback={<Loader color="black" />}>
                    Please select a Google Slides presentation only.
                  </Suspense> :
                  // <form onSubmit={form.onSubmit((values) => extractId(values))}>
                  <form onSubmit={form.onSubmit((values) => handleSubmitGoogleURL(values.url))}>
                    <TextInput
                      withAsterisk
                      label="Google Slides URL"
                      placeholder="Enter your presentation URL"
                      {...form.getInputProps('url')}
                    />
                    <Button style={{
                      width: 100, marginTop: 20, marginBottom: 20, paddingBottom: 1, inline: true, backgroundColor: 'black', borderRadius: 4
                    }} type="submit">Submit</Button>
                  </form>}
            </>
              : <>
                <GoogleShareButton projectKey={projectKey} /></>

            }
          </div>

        </Tabs.Panel>
      </Tabs>
      {/* </Card>  */}
    </Modal>

  );
}
